import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import {
  Box,
  Container,
  Grid,
  Icon,
  Typography,
  useMediaQuery,
} from '@mui/material';
import React from 'react';

const MobileView = ({ daysLeft, formattedDueDate, title }) => {
  return (
    <>
      <Grid
        item
        xs={12}
        md={12}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          mt: '24px',
          textAlign: 'center',
        }}>
        <Typography
          sx={{
            fontSize: { xs: '42px', sm: '56px', md: '64px' },
            fontWeight: '300',
            textAlign: 'center',
            mt: '24px',
          }}>
          {title}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            ml: { xs: 0, md: 3 },
            pt: '16px',
          }}>
          <Typography
            variant="span"
            sx={{ fontSize: { xs: '20px', md: '24px' } }}>
            <img
              alt="due date"
              src="/calendar-heart.svg"
              style={{
                fontSize: { xs: 16, md: 20 },
                mr: 1,
                paddingRight: '8px',
              }}
            />
            <Typography variant="span" sx={{ fontWeight: 600 }}>
              {daysLeft}
            </Typography>{' '}
            days to go
          </Typography>
          <Typography sx={{ fontSize: { xs: '13px' } }}>
            Due date: {formattedDueDate}
          </Typography>
        </Box>
      </Grid>
    </>
  );
};

const DesktopView = ({
  title,
  user_profile_pic,
  daysLeft,
  formattedDueDate,
}) => {
  return (
    <>
      <Grid
        item
        xs={12}
        md={12}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
        }}>
        <Box
          sx={{
            borderRadius: '50%',
            width: '132px',
            height: '132px',
            mb: { xs: '20px', md: 0 },
          }}>
          <img
            src={user_profile_pic || '/default_profile_pic.png'}
            loading="lazy"
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
            }}
            alt="Registry"
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            ml: { xs: 0, md: 3 },
          }}>
          <Typography
            variant="span"
            sx={{ fontSize: { xs: '20px', md: '24px' } }}>
            <img
              alt="due date"
              src="/calendar-heart.svg"
              style={{
                fontSize: { xs: 16, md: 20 },
                mr: 1,
                paddingRight: '8px',
              }}
            />
            <Typography variant="span" sx={{ fontWeight: 600 }}>
              {daysLeft}
            </Typography>{' '}
            days to go
          </Typography>
          <Typography sx={{ fontSize: { xs: '12px', md: '13px' } }}>
            Due date: {formattedDueDate}
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12} md={12} pt="16px">
        <Typography
          sx={{
            fontSize: { xs: '42px', sm: '56px', md: '64px' },
            fontWeight: '300',
            lineHeight: '80px',
            letterSpacing: '0em',
            textAlign: 'left',
          }}>
          {title}
        </Typography>
      </Grid>
    </>
  );
};

const RegistryDetails = ({ registryData }) => {
  const isSmallScreen = useMediaQuery('(max-width:500px)'); // xs

  if (!registryData) {
    return null;
  }
  const {
    title,
    due_date,
    user_profile_pic,
    cover_photo,
    description,
    shipping_address,
    last_name,
  } = registryData;

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const currentDate = new Date();

  const convertDueDate = (date) => {
    var [mm, dd, yyyy] = date.split('-');
    let newDate = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd));
    return newDate;
  };

  const dueDate = convertDueDate(due_date);

  const day = dueDate.getDate();
  const formattedDueDate = (
    <>
      {months[dueDate.getMonth()]} {day}
      <sup>{getOrdinalSuffix(day)}</sup>, {dueDate.getFullYear()}
    </>
  );

  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  const daysLeft = Math.ceil((dueDate - currentDate) / millisecondsPerDay);

  function getOrdinalSuffix(day) {
    if (day >= 11 && day <= 13) {
      return 'th';
    }
    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }

  const prepopulatedData = `Thank you so much for visiting our baby registry! 
  We really appreciate all the support we've received from friends and family since we learned the exciting news. 
  We can't wait to introduce you Baby ${last_name}!`;

  return (
    <>
      <Container maxWidth sx={{ px: { xs: 0, sm: 0 } }}>
        <Grid container>
          {isSmallScreen && (
            <>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'var(--White, #FFF)',
                }}>
                <img
                  src="/m&d_logo.png"
                  alt="M&D Logo"
                  style={{
                    height: '24px',
                    width: 'auto',
                    margin: '20px',
                  }}
                />
              </Box>
              <Box
                sx={{
                  width: '100%',
                  height: '240px',
                  position: 'relative',
                  overflow: 'visible',
                }}>
                <img
                  src={cover_photo ? cover_photo : '/default_cover.jpg'}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                  alt="Cover"
                />

                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '-52px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    borderRadius: '50%',
                    overflow: 'visible',
                    border: '8px solid var(--White, #FFF)',
                  }}>
                  <img
                    src={user_profile_pic || '/default_profile_pic.png'}
                    loading="lazy"
                    style={{
                      width: '104px',
                      height: '104px',
                      borderRadius: '50%',
                    }}
                    alt="Profile"
                  />
                </Box>
              </Box>
            </>
          )}

          <Grid
            item
            xs={12}
            sm={12}
            md={5}
            sx={{
              backgroundColor: '#FFFFFF',
              p: { xs: '24px', sm: '40px', md: '80px' },
              pb: { xs: '40px' },
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: '24px' },
            }}>
            {isSmallScreen ? (
              <MobileView
                user_profile_pic={user_profile_pic}
                formattedDueDate={formattedDueDate}
                daysLeft={daysLeft < 0 ? 0 : daysLeft}
                title={title}
                cover_photo={cover_photo}
              />
            ) : (
              <DesktopView
                user_profile_pic={user_profile_pic}
                formattedDueDate={formattedDueDate}
                daysLeft={daysLeft < 0 ? 0 : daysLeft}
                title={title}
              />
            )}
            <Grid
              item
              md={12}
              sx={{
                py: '24px',
                position: 'relative',
                padding: '16px',
                backgroundImage: `
                url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='33' height='33' viewBox='0 0 33 33' fill='none'%3E%3Cpath d='M32 33V33C32 15.3269 17.6731 1 0 1V1' stroke='black'/%3E%3C/svg%3E"),
                url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='33' height='33' viewBox='0 0 33 33' fill='none'%3E%3Cpath d='M33 1V1C15.3269 1 1 15.3269 1 33V33' stroke='black'/%3E%3C/svg%3E"),
                url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='33' height='33' viewBox='0 0 33 33' fill='none'%3E%3Cpath d='M0.999999 0V0C0.999999 17.6731 15.3269 32 33 32V32' stroke='black'/%3E%3C/svg%3E"),
                url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='33' height='33' viewBox='0 0 33 33' fill='none'%3E%3Cpath d='M0 32V32C17.6731 32 32 17.6731 32 0V0' stroke='black'/%3E%3C/svg%3E")
              `,
                backgroundRepeat: 'no-repeat',
                backgroundPosition:
                  'top right, top left, bottom left, bottom right',
                backgroundSize: '33px 33px',
              }}>
              <Typography
                sx={{
                  fontSize: '16px',
                  padding: { xs: '16px', sm: '24px' },
                  backgroundColor: '#EADFE7',
                  borderRadius: 4,
                }}>
                {description ? description : prepopulatedData}
              </Typography>
            </Grid>
            {shipping_address && (
              <Grid item xs={12} md={12} sx={{ display: 'flex', mt: 2 }}>
                <Box
                  sx={{
                    borderRadius: '50%',
                    width: { xs: '40px', sm: '56px' },
                    height: { xs: '40px', sm: '56px' },
                    background: 'var(--Salmon, #F5BE9F)',
                  }}>
                  <Icon
                    sx={{
                      width: 'inherit',
                      height: 'inherit',
                      borderRadius: '50%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}>
                    <LocalShippingOutlinedIcon />
                  </Icon>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    ml: 3,
                  }}>
                  <Typography variant="span" sx={{ fontWeight: 600 }}>
                    Shipping address
                  </Typography>
                  <Typography sx={{ fontSize: 13 }}>
                    {shipping_address}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
          {!isSmallScreen && (
            <Grid
              item
              xs
              sx={{
                backgroundImage: `url(${
                  cover_photo ? cover_photo : '/default_cover.jpg'
                })`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}></Grid>
          )}
        </Grid>
      </Container>
    </>
  );
};

export default RegistryDetails;
